import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Bell, Check } from "lucide-react";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications", { params: { limit: 20 } });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (_e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const markRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); };
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => { setOpen((v) => !v); if (!open) load(); }}
        className="relative w-9 h-9 grid place-items-center rounded-full border border-border hover:bg-muted"
        data-testid="notification-bell"
      >
        <Bell className="w-4 h-4" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold grid place-items-center" data-testid="unread-count">{unread}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-96 bg-card border border-border rounded-lg shadow-lg z-50" data-testid="notification-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="font-display font-bold">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll} className="text-xs text-primary font-semibold hover:underline" data-testid="mark-all-read">Mark all read</button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-6 text-sm text-muted-foreground text-center">You&apos;re all caught up 🎉</div>
            ) : items.map((n) => (
              <div key={n.id} className={`px-4 py-3 border-b border-border last:border-0 ${!n.read_at ? "bg-secondary/40" : ""}`} data-testid={`notification-${n.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold">{n.title}</div>
                    {n.body && <div className="text-xs text-muted-foreground mt-0.5">{n.body}</div>}
                    <div className="flex items-center gap-3 mt-1">
                      {n.link && <Link to={n.link} onClick={() => setOpen(false)} className="text-xs text-primary font-semibold hover:underline">View →</Link>}
                      <span className="text-[10px] font-mono text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span>
                    </div>
                  </div>
                  {!n.read_at && (
                    <button onClick={() => markRead(n.id)} className="text-muted-foreground hover:text-foreground" title="Mark read">
                      <Check className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
